import React, { useEffect, useMemo, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  BadgeCheck,
  Blocks,
  Box,
  Briefcase,
  Building2,
  CircleHelp,
  Cloud,
  Cpu,
  Database,
  FileText,
  FolderKanban,
  FolderOpen,
  Globe2,
  Layers,
  LayoutGrid,
  Network,
  PlusCircle,
  Pencil,
  Sparkles,
  RefreshCcw,
  Table2,
  TableProperties,
  Globe,
  PlugZap,
  Upload,
  Server,
  Table,
  FileSpreadsheet,
  Tags,
  Trash2,
  UserCog,
  ShieldCheck,
  UserCircle2,
  Workflow,
  Search,
  X,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
} from 'lucide-react';
import '../../workflows/Workflows.css';
import './data-ingestion.css';
import SmartMetadataModal from './SmartMetadataModal';
import { ingestionService } from '../../../../components/api/ingestionService';
import ConfirmDialog from '../../Dialogs/ConfirmDialog';
import RenameModal from '../../common/RenameModal';
import PermissionsAssignmentModal from '../../common/Permissions/PermissionsAssignmentModal';
import IngestionEntityModal from './popups/IngestionEntityModal';
import type { IngestionEntityPayload, DataModelResponse } from './popups/IngestionEntityModal';
import { apiService } from '../../Connections/connectionsApiService';
import { useToast } from '../../../../components/common/Toast';
import DataModelGroupsModal from './DataModelGroupsModal';

const initialDataSources: never[] = [];

type DataModelGroupOption = { uuid: string; name: string; icon?: string; status?: string };

const GROUP_SIDEBAR_ICON_BY_KEY: Record<string, LucideIcon> = {
  LayoutGrid,
  Database,
  Layers,
  FolderKanban,
  Blocks,
  Table2,
  Workflow,
  Network,
  Cloud,
  Cpu,
  Globe2,
  Building2,
  Briefcase,
  FileText,
  Box,
  Tags,
  ShieldCheck,
  BadgeCheck,
  Sparkles,
  Activity,
  UserCircle2,
};

function GroupSidebarIcon({ iconRef }: { iconRef: string | undefined }) {
  const name = (iconRef ?? 'LayoutGrid').trim();
  const Icon = GROUP_SIDEBAR_ICON_BY_KEY[name] ?? CircleHelp;
  return <Icon strokeWidth={1.75} aria-hidden />;
}

const sourceTypes = [
  { label: 'SqlServer', icon: <Database className="w-4 h-4 text-cyan-400" /> },
  { label: 'PostgreSQL', icon: <Database className="w-4 h-4 text-cyan-500" /> },
  { label: 'MySQL', icon: <Database className="w-4 h-4 text-blue-500" /> },
  { label: 'Oracle', icon: <Database className="w-4 h-4 text-red-500" /> },
  { label: 'SQLite', icon: <Database className="w-4 h-4 text-gray-400" /> },
  { label: 'NoSQL_MongoDB', icon: <TableProperties className="w-4 h-4 text-green-400" /> },
  { label: 'API', icon: <Globe className="w-4 h-4 text-yellow-400" /> },
  { label: 'Webhook', icon: <PlugZap className="w-4 h-4 text-pink-400" /> },
  { label: 'CSV_Upload', icon: <Upload className="w-4 h-4 text-orange-400" /> },
  { label: 'S3_Bucket', icon: <Cloud className="w-4 h-4 text-blue-400" /> },
  { label: 'Azure_Blob', icon: <Cloud className="w-4 h-4 text-blue-300" /> },
  { label: 'Google_Sheets', icon: <TableProperties className="w-4 h-4 text-green-300" /> },
  { label: 'FTP_SFTP', icon: <Server className="w-4 h-4 text-purple-400" /> },
  { label: 'Firestore', icon: <TableProperties className="w-4 h-4 text-red-400" /> },
  { label: 'Snowflake', icon: <Cloud className="w-4 h-4 text-white" /> },
  { label: 'CSV', icon: <Table className="w-4 h-4 text-white" /> },
  { label: 'Excel', icon: <FileSpreadsheet className="w-4 h-4 text-white" /> },
];

const PAGE_SIZE_OPTIONS = [10, 25, 50] as const;
const DEFAULT_PAGE_SIZE = 10;

const IngestionList = () => {
  const [activeMetadataSource, setActiveMetadataSource] = useState<any>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'All' | 'Active' | 'Disabled'>('All');
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [page, setPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editInitialData, setEditInitialData] = useState<DataModelResponse | null>(null);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [dsToDelete, setdsToDelete] = useState(false);
  const [selectedDSID, setSelectedDSID] = useState('');
  const [selectedDSName, setSelectedDSName] = useState('');

  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renameTarget, setRenameTarget] = useState<{ id: string; name: string } | null>(null);

  const [showAssignmentModal, setAssignmentModal] = useState(false);
  const { showToast } = useToast();
  const [showGroupsModal, setShowGroupsModal] = useState(false);

  const [dataSources, setDataSources] = useState<any[]>(initialDataSources);
  const [loadingDataModels, setLoadingDataModels] = useState(true);
  const [dataModelGroups, setDataModelGroups] = useState<DataModelGroupOption[]>([]);
  const [loadingGroupsSidebar, setLoadingGroupsSidebar] = useState(true);
  const [selectedGroupKey, setSelectedGroupKey] = useState<string | '__all__' | '__ungrouped__'>('__all__');

  function mapDataModelToCard(item: {
    id: string;
    modelName: string;
    description?: string;
    type?: string;
    status?: string;
    tags?: string[];
    groupUuid?: string;
    group_uuid?: string;
  }) {
    return {
      id: item.id,
      name: item.modelName,
      description: item.description || '',
      type: item.type || '—',
      status: item.status || 'Active',
      schedule: 'Manual Trigger',
      tags: Array.isArray(item.tags) ? item.tags : [],
      lastSynced: '—',
      groupUuid: item.groupUuid ?? item.group_uuid ?? '',
    };
  }

  const fetchDataModels = async () => {
    setLoadingDataModels(true);
    try {
      const data = await apiService.get('/DataModels', {}, 'backend');
      const list = data?.dataModels ?? [];
      setDataSources(list.map(mapDataModelToCard));
    } catch (err) {
      console.error('Failed to load data models:', err);
      setDataSources([]);
    } finally {
      setLoadingDataModels(false);
    }
  };

  const loadDataSources = async () => {
    try {
      const tenantId = '3fa85f64-5717-4562-b3fc-2c963f66afa6';
      const apiResults = await ingestionService.loadDataSources(tenantId);
      const mapped = apiResults.map(mapApiToDataSource);
      setDataSources((prev) => {
        const combined = [...mapped];
        const unique = Array.from(new Map(combined.map((item) => [item.id, item])).values());
        return unique;
      });
    } catch (err) {
      console.error('Failed to load data sources:', err);
    }
  };

  const mapApiToDataSource = (apiItem: any): any => {
    return {
      id: apiItem.id,
      name: apiItem.connectionName,
      type: resolveSourceType(apiItem.sourceType),
      status: apiItem.isActive ? 'Active' : 'Disabled',
      description: apiItem.description || '',
      schedule: 'Manual Trigger',
      tags: apiItem.tags
        ? apiItem.tags
            .split(',')
            .map((tag: string) => tag.trim())
            .filter((tag: string) => Boolean(tag))
        : [],
      lastSynced: apiItem.lastSyncedAt ? new Date(apiItem.lastSyncedAt).toLocaleString() : 'Never',
    };
  };

  const handleDeleteDataModel = async (id: string): Promise<void> => {
    await apiService.delete(`/DataModels/${id}`, 'backend');
    fetchDataModels();
  };

  const resolveSourceType = (index: number) => {
    const source = sourceTypes[index];
    return source ? source.label : `Type ${index}`;
  };

  const fetchDataModelGroups = async () => {
    setLoadingGroupsSidebar(true);
    try {
      const response = await apiService.get('/datastores/groups', {}, 'backend');
      const raw = Array.isArray(response)
        ? response
        : Array.isArray(response?.groups)
          ? response.groups
          : [];
      setDataModelGroups(
        raw.map((g: { uuid: string; name: string; icon?: string; status?: string }) => ({
          uuid: g.uuid,
          name: g.name,
          icon: g.icon,
          status: g.status,
        })),
      );
    } catch (err) {
      console.error('Failed to load groups for filter:', err);
      setDataModelGroups([]);
    } finally {
      setLoadingGroupsSidebar(false);
    }
  };

  useEffect(() => {
    fetchDataModels();
  }, []);

  useEffect(() => {
    fetchDataModelGroups();
  }, []);

  useEffect(() => {
    if (!showAddModal || !editId) return;
    let cancelled = false;
    setLoadingEdit(true);
    apiService
      .get(`/DataModels/${editId}`, {}, 'backend')
      .then((raw: DataModelResponse | { dataModel?: DataModelResponse; data?: DataModelResponse }) => {
        if (cancelled) return;
        const data =
          raw && typeof raw === 'object' && 'dataModel' in raw && raw.dataModel
            ? raw.dataModel
            : raw && typeof raw === 'object' && 'data' in raw && raw.data
              ? raw.data
              : (raw as DataModelResponse);
        setEditInitialData(data);
      })
      .catch((err) => {
        if (!cancelled)
          showToast({
            type: 'error',
            message: err.response?.data?.message || err.message || 'Failed to load data model.',
          });
      })
      .finally(() => {
        if (!cancelled) setLoadingEdit(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showAddModal, editId]);

  const effectiveGroupKey = useMemo(() => {
    if (selectedGroupKey === '__all__') return '__all__';
    if (selectedGroupKey === '__ungrouped__') return '__ungrouped__';
    if (dataModelGroups.some((g) => g.uuid === selectedGroupKey)) return selectedGroupKey;
    return '__all__';
  }, [selectedGroupKey, dataModelGroups]);

  const groupCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const s of dataSources) {
      const key = s.groupUuid || '__ungrouped__';
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return counts;
  }, [dataSources]);

  const rowsForGroup = useMemo(() => {
    if (effectiveGroupKey === '__all__') return dataSources;
    if (effectiveGroupKey === '__ungrouped__') {
      return dataSources.filter((s) => !s.groupUuid);
    }
    return dataSources.filter((s) => s.groupUuid === effectiveGroupKey);
  }, [dataSources, effectiveGroupKey]);

  const groupNameForCard = (groupUuid: string) => dataModelGroups.find((g) => g.uuid === groupUuid)?.name ?? '';

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rowsForGroup.filter((s) => {
      if (statusFilter !== 'All' && s.status !== statusFilter) return false;
      if (!q) return true;
      const groupName = s.groupUuid ? groupNameForCard(s.groupUuid) : '';
      const haystack = [s.name, s.description ?? '', s.type ?? '', s.status ?? '', groupName, ...(s.tags ?? [])]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [rowsForGroup, search, statusFilter, dataModelGroups]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));

  const paginated = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, pageSize, effectiveGroupKey]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const rangeStart = filtered.length === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, filtered.length);

  const selectedGroupLabel =
    effectiveGroupKey === '__all__'
      ? 'All groups'
      : effectiveGroupKey === '__ungrouped__'
        ? 'No group'
        : (dataModelGroups.find((g) => g.uuid === effectiveGroupKey)?.name ?? 'Group');

  const activeCount = useMemo(() => dataSources.filter((s) => s.status === 'Active').length, [dataSources]);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleSaveEntity = async (data: IngestionEntityPayload) => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      if (editId) {
        console.info('[DataModels]', '[app]', { event: 'update_data_model', id: editId, hasAdditionalContext: Boolean(data.additionalContext) });
        await apiService.put(`/DataModels/${editId}`, data, 'backend');
        showToast({ type: 'success', message: 'Data model updated successfully.' });
        setShowAddModal(false);
        setEditId(null);
        setEditInitialData(null);
        fetchDataModels();
      } else {
        const response = await apiService.postWithResponse('/DataModels', data, 'backend');
        if (response.status === 201 || (response.status >= 200 && response.status < 300)) {
          showToast({ type: 'success', message: 'Data model created successfully.' });
          setShowAddModal(false);
          fetchDataModels();
        } else {
          const msg = response.data?.message || 'Something went wrong.';
          setSubmitError(msg);
          showToast({ type: 'error', message: msg });
        }
      }
    } catch (err: any) {
      const message =
        err.response?.data?.message ||
        err.message ||
        (editId ? 'Failed to update data model.' : 'Failed to create data model.');
      setSubmitError(message);
      showToast({ type: 'error', message });
      console.error('Error saving entity:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const openCreateModal = () => {
    setEditId(null);
    setEditInitialData(null);
    setLoadingEdit(false);
    setSubmitError(null);
    setShowAddModal(true);
  };

  const openEditModal = (source: (typeof dataSources)[number]) => {
    setSubmitError(null);
    setEditId(source.id);
    setEditInitialData(null);
    setLoadingEdit(true);
    setShowAddModal(true);
  };

  return (
    <div className="wf-page">
      <header className="wf-top">
        <div className="wf-top__inner">
          <div className="wf-top__content">
            <p className="wf-top__eyebrow">Configuration</p>
            <h1 className="wf-top__title">Data Models</h1>
            {!loadingDataModels && (
              <div className="wf-top__stats" aria-label="Data model summary">
                <span className="wf-top__stat">
                  <Database className="w-3.5 h-3.5" aria-hidden />
                  {dataSources.length} total
                </span>
                <span className="wf-top__stat">
                  <Layers className="w-3.5 h-3.5" aria-hidden />
                  {dataModelGroups.length} group{dataModelGroups.length === 1 ? '' : 's'}
                </span>
                {activeCount > 0 ? (
                  <span className="wf-top__stat wf-top__stat--live">
                    <Activity className="w-3.5 h-3.5" aria-hidden />
                    {activeCount} active
                  </span>
                ) : null}
              </div>
            )}
          </div>
          <div className="wf-top__actions">
            <button type="button" className="wf-btn wf-btn--ghost" onClick={() => setShowGroupsModal(true)}>
              <TableProperties className="w-4 h-4" aria-hidden />
              Manage Groups
            </button>
            <button type="button" className="wf-btn wf-btn--primary" onClick={openCreateModal}>
              <PlusCircle className="w-4 h-4" aria-hidden />
              Add Source
            </button>
          </div>
        </div>
      </header>

      <div className="wf-shell">
        <aside className="wf-projects" aria-label="Data model groups">
          <div className="wf-projects__head">
            <FolderKanban className="w-4 h-4" aria-hidden />
            <span>Groups</span>
          </div>
          <nav className="wf-projects__list">
            {loadingGroupsSidebar ? (
              <p className="wf-projects__empty">Loading groups…</p>
            ) : (
              <>
                <button
                  type="button"
                  className={`wf-projects__item wf-projects__item--all${effectiveGroupKey === '__all__' ? ' wf-projects__item--active' : ''}`}
                  onClick={() => setSelectedGroupKey('__all__')}
                >
                  <span className="wf-projects__item-icon" aria-hidden>
                    <LayoutGrid strokeWidth={1.75} />
                  </span>
                  <span className="wf-projects__item-label">All groups</span>
                  <span className="wf-projects__item-count">{dataSources.length}</span>
                </button>

                {dataModelGroups.map((g) => {
                  const active = effectiveGroupKey === g.uuid;
                  const count = groupCounts.get(g.uuid) ?? 0;
                  return (
                    <button
                      key={g.uuid}
                      type="button"
                      className={`wf-projects__item${active ? ' wf-projects__item--active' : ''}`}
                      onClick={() => setSelectedGroupKey(g.uuid)}
                    >
                      <span className="wf-projects__item-icon" aria-hidden>
                        <GroupSidebarIcon iconRef={g.icon} />
                      </span>
                      <span className="wf-projects__item-label">{g.name}</span>
                      <span className="wf-projects__item-count">{count}</span>
                    </button>
                  );
                })}

                <button
                  type="button"
                  className={`wf-projects__item wf-projects__item--all${effectiveGroupKey === '__ungrouped__' ? ' wf-projects__item--active' : ''}`}
                  onClick={() => setSelectedGroupKey('__ungrouped__')}
                >
                  <span className="wf-projects__item-icon" aria-hidden>
                    <FolderOpen strokeWidth={1.75} />
                  </span>
                  <span className="wf-projects__item-label">No group</span>
                  <span className="wf-projects__item-count">{groupCounts.get('__ungrouped__') ?? 0}</span>
                </button>
              </>
            )}
          </nav>
        </aside>

        <section className="wf-main" aria-label="Data model registry">
          <div className="wf-main__head">
            <h2 className="wf-main__title">{selectedGroupLabel}</h2>
            <p className="wf-main__meta">
              {loadingDataModels ? (
                'Loading…'
              ) : (
                <>
                  <Database className="w-3.5 h-3.5" aria-hidden />
                  {filtered.length} of {rowsForGroup.length} model{rowsForGroup.length === 1 ? '' : 's'}
                  {search.trim() ? ' matching search' : ''}
                </>
              )}
            </p>
          </div>

          <div className="wf-toolbar">
            <div className="wf-toolbar__search">
              <Search className="wf-toolbar__search-icon" aria-hidden />
              <input
                type="search"
                className="wf-toolbar__search-input"
                placeholder="Search by name, description, tags, type…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search data models"
              />
              {search ? (
                <button
                  type="button"
                  className="wf-toolbar__search-clear"
                  onClick={() => setSearch('')}
                  aria-label="Clear search"
                >
                  <X className="w-3.5 h-3.5" aria-hidden />
                </button>
              ) : null}
            </div>

            <div className="wf-toolbar__controls">
              <label className="wf-toolbar__field">
                <span className="wf-toolbar__field-label">Status</span>
                <span className="wf-toolbar__select-wrap">
                  <select
                    className="wf-toolbar__select"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as 'All' | 'Active' | 'Disabled')}
                    aria-label="Filter by status"
                  >
                    <option value="All">All</option>
                    <option value="Active">Active</option>
                    <option value="Disabled">Disabled</option>
                  </select>
                  <ChevronDown className="wf-toolbar__select-chevron" aria-hidden />
                </span>
              </label>

              <label className="wf-toolbar__field">
                <span className="wf-toolbar__field-label">Per page</span>
                <span className="wf-toolbar__select-wrap">
                  <select
                    className="wf-toolbar__select"
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                    aria-label="Rows per page"
                  >
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="wf-toolbar__select-chevron" aria-hidden />
                </span>
              </label>

              <button
                type="button"
                className="wf-toolbar__refresh"
                onClick={() => void fetchDataModels()}
                disabled={loadingDataModels}
                title="Refresh list"
                aria-label="Refresh list"
              >
                <RefreshCcw className={`w-4 h-4 ${loadingDataModels ? 'animate-spin' : ''}`} aria-hidden />
              </button>
            </div>
          </div>

          <div className="wf-table-card">
            {loadingDataModels ? (
              <div className="wf-loading" aria-busy="true">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="wf-loading__row" />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="wf-empty">
                <div className="wf-empty__icon-wrap" aria-hidden>
                  <Database strokeWidth={1.5} />
                </div>
                <p className="wf-empty__title">
                  {rowsForGroup.length === 0 ? 'No data models in this group' : 'No matching data models'}
                </p>
                <p className="wf-empty__text">
                  {rowsForGroup.length === 0
                    ? 'Add a data source or assign models to this group.'
                    : 'Try a different search term, status filter, or switch groups.'}
                </p>
                {(search || statusFilter !== 'All') && (
                  <button
                    type="button"
                    className="wf-btn wf-btn--outline wf-btn--sm"
                    onClick={() => {
                      setSearch('');
                      setStatusFilter('All');
                    }}
                  >
                    Clear filters
                  </button>
                )}
                {rowsForGroup.length === 0 && dataSources.length === 0 && (
                  <button type="button" className="wf-btn wf-btn--primary" onClick={openCreateModal}>
                    <PlusCircle className="w-4 h-4" aria-hidden />
                    Add Source
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="wf-table-wrap">
                  <table className="wf-table">
                    <thead>
                      <tr>
                        <th>
                          <span className="wf-th">
                            <Database className="w-3.5 h-3.5" aria-hidden />
                            Name
                          </span>
                        </th>
                        <th className="wf-col-hide-md">
                          <span className="wf-th">
                            <FileText className="w-3.5 h-3.5" aria-hidden />
                            Description
                          </span>
                        </th>
                        <th>
                          <span className="wf-th">
                            <TableProperties className="w-3.5 h-3.5" aria-hidden />
                            Type
                          </span>
                        </th>
                        <th className="wf-col-hide-lg">
                          <span className="wf-th">
                            <FolderKanban className="w-3.5 h-3.5" aria-hidden />
                            Group
                          </span>
                        </th>
                        <th>
                          <span className="wf-th">
                            <Activity className="w-3.5 h-3.5" aria-hidden />
                            Status
                          </span>
                        </th>
                        <th className="wf-col-hide-md">
                          <span className="wf-th">
                            <Tags className="w-3.5 h-3.5" aria-hidden />
                            Tags
                          </span>
                        </th>
                        <th className="wf-col-actions">
                          <span className="wf-th">
                            <MoreHorizontal className="w-3.5 h-3.5" aria-hidden />
                            Actions
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginated.map((source) => {
                        const isActive = source.status === 'Active';
                        const desc = source.description?.trim() || 'No description';
                        const groupLabel = source.groupUuid
                          ? groupNameForCard(source.groupUuid) || source.groupUuid
                          : '—';
                        const visibleTags = (source.tags || []).slice(0, 3);
                        const extraTags = Math.max(0, (source.tags?.length ?? 0) - visibleTags.length);

                        return (
                          <tr key={source.id} className={isActive ? 'wf-row--live' : undefined}>
                            <td>
                              <button type="button" className="wf-flow-cell" onClick={() => openEditModal(source)}>
                                <span className="wf-flow-cell__icon">
                                  <Database className="wf-flow-icon" aria-hidden />
                                </span>
                                <span className="wf-flow-cell__text">
                                  <span className="wf-flow-cell__name">{source.name}</span>
                                  <span className="wf-flow-cell__sub">{source.type}</span>
                                </span>
                              </button>
                            </td>
                            <td className="wf-col-hide-md">
                              <span className="wf-cell-muted" title={desc}>
                                {desc}
                              </span>
                            </td>
                            <td>
                              <span className="dm-type-cell">
                                <span className="dm-type-cell__icon">
                                  <Database aria-hidden />
                                </span>
                                {source.type}
                              </span>
                            </td>
                            <td className="wf-col-hide-lg">
                              <span className="wf-cell-muted" title={groupLabel !== '—' ? groupLabel : undefined}>
                                {groupLabel}
                              </span>
                            </td>
                            <td>
                              <span
                                className={`wf-status ${isActive ? 'wf-status--live' : 'wf-status--disabled'}`}
                              >
                                <span className="wf-status__dot" aria-hidden />
                                {source.status}
                              </span>
                            </td>
                            <td className="wf-col-hide-md">
                              {(source.tags?.length ?? 0) > 0 ? (
                                <div className="dm-tags">
                                  {visibleTags.map((tag: string) => (
                                    <span key={tag} className="dm-tag">
                                      {tag}
                                    </span>
                                  ))}
                                  {extraTags > 0 ? <span className="dm-tag">+{extraTags}</span> : null}
                                </div>
                              ) : (
                                <span className="wf-cell-muted">—</span>
                              )}
                            </td>
                            <td className="wf-col-actions">
                              <div className="wf-row-menu">
                                <button
                                  type="button"
                                  className="wf-row-menu__btn"
                                  title="Manage Permissions & Access"
                                  aria-label={`Permissions for ${source.name}`}
                                  onClick={() => {
                                    setSelectedDSID(source.id);
                                    setSelectedDSName(source.name || '');
                                    setAssignmentModal(true);
                                  }}
                                >
                                  <UserCog className="w-4 h-4" aria-hidden />
                                </button>
                                <button
                                  type="button"
                                  className="wf-row-menu__btn"
                                  title="Test Sync"
                                  aria-label={`Test sync for ${source.name}`}
                                >
                                  <RefreshCcw className="w-4 h-4" aria-hidden />
                                </button>
                                <button
                                  type="button"
                                  className="wf-row-menu__btn"
                                  title="Edit data model"
                                  aria-label={`Edit ${source.name}`}
                                  onClick={() => openEditModal(source)}
                                >
                                  <Pencil className="w-4 h-4" aria-hidden />
                                </button>
                                <button
                                  type="button"
                                  className="wf-row-menu__btn wf-row-menu__btn--danger"
                                  title="Delete data model"
                                  aria-label={`Delete ${source.name}`}
                                  onClick={() => {
                                    setSelectedDSID(source.id);
                                    setSelectedDSName(source.name || '');
                                    setdsToDelete(true);
                                  }}
                                >
                                  <Trash2 className="w-4 h-4" aria-hidden />
                                </button>
                                <button
                                  type="button"
                                  className="wf-row-menu__btn"
                                  title="Smart Metadata Detection"
                                  aria-label={`Metadata for ${source.name}`}
                                  onClick={() => setActiveMetadataSource(source)}
                                >
                                  <Sparkles className="w-4 h-4" aria-hidden />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <footer className="wf-foot" aria-label="Pagination">
                  <span className="wf-foot__range">
                    {rangeStart}–{rangeEnd} of {filtered.length}
                  </span>
                  <div className="wf-foot__nav">
                    <button
                      type="button"
                      className="wf-foot__btn"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page <= 1}
                      aria-label="Previous page"
                    >
                      <ChevronLeft className="w-4 h-4" aria-hidden />
                    </button>
                    <span className="wf-foot__page">
                      Page {page} of {totalPages}
                    </span>
                    <button
                      type="button"
                      className="wf-foot__btn"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages}
                      aria-label="Next page"
                    >
                      <ChevronRight className="w-4 h-4" aria-hidden />
                    </button>
                  </div>
                </footer>
              </>
            )}
          </div>
        </section>
      </div>

      {showAddModal && (
        <IngestionEntityModal
          onClose={() => {
            setShowAddModal(false);
            setSubmitError(null);
            setEditId(null);
            setEditInitialData(null);
            setLoadingEdit(false);
          }}
          onSubmit={handleSaveEntity}
          submitting={submitting}
          submitError={submitError}
          editId={editId}
          initialData={editInitialData}
          loadingEdit={loadingEdit}
        />
      )}

      {dsToDelete && (
        <ConfirmDialog
          title="Delete data model"
          message={
            selectedDSName
              ? `Are you sure you want to delete "${selectedDSName}"? This action cannot be undone.`
              : 'Are you sure you want to delete this data model? This action cannot be undone.'
          }
          onYes={async () => {
            try {
              await handleDeleteDataModel(selectedDSID);
              setdsToDelete(false);
              showToast({ type: 'success', message: 'Data model deleted successfully.' });
            } catch (err: any) {
              showToast({
                type: 'error',
                message: err.response?.data?.message || err.message || 'Failed to delete data model.',
              });
            }
          }}
          onNo={() => setdsToDelete(false)}
          onCancel={() => setdsToDelete(false)}
        />
      )}

      {activeMetadataSource && (
        <SmartMetadataModal dataSource={activeMetadataSource} onClose={() => setActiveMetadataSource(null)} />
      )}

      {renameTarget && showRenameModal && (
        <RenameModal
          id={renameTarget.id}
          currentName={renameTarget.name}
          module="Ingestion Source"
          onCancel={() => {
            setShowRenameModal(false);
            setRenameTarget(null);
          }}
          onSave={({ id, newName }) => {
            ingestionService
              .renameIngestion({
                id,
                tenantID: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
                newTitle: newName,
              })
              .then(() => {
                loadDataSources();
                setShowRenameModal(false);
                setRenameTarget(null);
              });
          }}
        />
      )}

      {showAssignmentModal && (
        <PermissionsAssignmentModal
          onClose={() => setAssignmentModal(false)}
          onSave={() => {}}
          entityType="entity"
          entityName={selectedDSName || 'System Entity'}
        />
      )}

      {showGroupsModal && (
        <DataModelGroupsModal
          onClose={() => {
            setShowGroupsModal(false);
            fetchDataModelGroups();
          }}
          onSuccess={(message) => showToast({ type: 'success', message })}
          onError={(message) => showToast({ type: 'error', message })}
        />
      )}
    </div>
  );
};

export default IngestionList;
